import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";
const KEY = "poplista.theme";

const current = (): Theme => (document.documentElement.dataset.theme === "dark" ? "dark" : "light");

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#050816" : "#0a1030");
}

/** Light/dark. Starts from the saved choice or the system setting (index.html sets it before first paint);
 *  without a saved choice it keeps following the system until the user picks one. */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(current);

  useEffect(() => {
    const media = matchMedia("(prefers-color-scheme: dark)");
    const follow = () => {
      try { if (localStorage.getItem(KEY)) return; } catch { /* storage blocked: follow the system */ }
      const next = media.matches ? "dark" : "light";
      apply(next);
      setTheme(next);
    };
    media.addEventListener("change", follow);
    return () => media.removeEventListener("change", follow);
  }, []);

  const toggle = useCallback(() => {
    const next: Theme = current() === "dark" ? "light" : "dark";
    apply(next);
    setTheme(next);
    try { localStorage.setItem(KEY, next); } catch { /* private mode: applies for this visit only */ }
  }, []);

  return { theme, toggle };
}
